import React, { useState, useEffect, Fragment } from 'react';
import { withStyles, makeStyles } from '@material-ui/core/styles';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';

import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import Radio from '@material-ui/core/Radio';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Zoom from '@material-ui/core/Zoom';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';

import CloseIcon from '@material-ui/icons/Close';
import CompleteIcon from '@material-ui/icons/CheckCircle';

import purple from '@material-ui/core/colors/purple';
import green from '@material-ui/core/colors/green';

import Loading from 'app/components/loaders/Loading';
import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogContent: {
        backgroundColor: theme.palette.grey[100],
        padding: 0
    },
    dialogContentText: {
        backgroundColor: '#fff',
        padding: theme.spacing(3)
    },
    list: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2)
    },
    listItem: {
        backgroundColor: '#ffffff',
        marginBottom: theme.spacing(1)
    },
    purple: {
        color: purple[600]
    },
    green: {
        color: green[600]
    },
    divider: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    gridContainer: {
        width: '100%',
        marginLeft: 0,
        marginRight: 0,
        marginTop: 0,
        marginBottom: 0
    },
    fieldLeft: {
        textAlign: 'right',
    },
    fieldRight: {
    },
    successIcon: {
        fontSize: '82px',
        color: theme.palette.success.main,
    },
    stepContainer: {
        padding: theme.spacing(3),
        textAlign: 'center',
        minHeight: '400px',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor: '#fff'
    },
}));

const GreenRadio = withStyles({
  root: {
    color: green[400],
    '&$checked': {
      color: green[600],
    },
  },
  checked: {},
})(props => <Radio color="default" {...props} />);

const PurpleRadio = withStyles({
  root: {
    color: purple[400],
    '&$checked': {
      color: purple[600],
    },
  },
  checked: {},
})(props => <Radio color="default" {...props} />);

const initialSelectedAccount = {id: 0};

const fieldMapUser = {
    username: 'Username',
    prefix: 'Prefix',
    first: 'First',
    middle: 'Middle',
    last: 'Last',
    suffix: 'Suffix',
    nickname: 'Nickname',
    marital_status: 'Marital Status',
    spouse: 'Spouse',
    address_one: 'Address One',
    address_two: 'Address Two',
    city: "City",
    state: "State",
    zip: "Zip",
    industry: "Industry",
    employer: "Employer",
    position: "Position",
    work_address_one: "Work Address One",
    work_address_two: "Work Address Two",
    work_city: "Work City",
    work_state: "Work State",
    work_zip: "Work Zip",
    phone_home: "Home Phone",
    phone_work: "Work Phone",
    phone_mobile: "Mobile Phone"
}

export default function ReconcileAccountsDialog(props) {

    const [ accounts, setAccounts ] = useState([]);
    const [ selectedAccount, setSelectedAccount ] = useState(initialSelectedAccount);
    const [ checkedFields, setCheckedFields ] = useState({})
    const [ step, setStep ] = useState(1);
    const [ error, setError ] = useState("");
    const [ loading, setLoading ] = useState(false);

    const { isOpen, onClose, onClosed, user } = props;

    const api = useApi();
    const classes = useStyles();

    const getAccounts = () => {
        setLoading(true);

        api.get('/auth/profile/reconcile/accounts')
            .then(res => setAccounts(res.accounts))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        getAccounts();
    }, []);

    const handleSelect = value => () => {
        const newSelectedAccount = (value.id !== selectedAccount.id) ? value : initialSelectedAccount;
        setSelectedAccount(newSelectedAccount);
    }

    const handleNext = () => {
        let nextStep = step + 1;

        // Checks
        switch (nextStep) {
            case 2:
                setInitialCheckedFields();
                break;
            case 3:
                nextStep = 2;
                postReconcileAccounts();
                break;
            case 4:
                nextStep = 3;
                onClose();
                break;

            default: break;
        }

        setStep(nextStep);
    }

    const handleBack = () => {
        let nextStep = step - 1;

        // Checks
        if (nextStep < 1) {
            nextStep = 1;
            onClose();
        };

        setStep(nextStep);
    }

    const isNextDisabled = () => {
        if (step === 1 && selectedAccount == null) return true;
        if (loading) return true;
        return false;
    }

    const isBackDisabled = () => {
        if (step === 3) return true;
        if (loading) return true;
        return false;
    }

    const nextText = () => {
        if (step == 2) return 'Merge Accounts';
        if (step == 3) return 'Close';
        return "Next";
    }

    const backText = () => {
        if (step === 1) return "Cancel";
        return "Back";
    }

    const postReconcileAccounts = () => {

        setLoading(true);
        setError("");

        const data = {
            merging_account: selectedAccount,
            checked_fields: checkedFields
        }

        api.post('/auth/profile/reconcile/accounts', data).then(res => {

            setStep(3);

        }).catch(error => {

            const errors = error.response.data.errors;

            if (errors && errors.accounts) {
                setError(errors.accounts[0]);
            }

        }).then(() => {
            setLoading(false);
        });
    }

    const clearState = () => {
        setAccounts([]);
        setSelectedAccount(initialSelectedAccount);
        setCheckedFields({});
        setStep(1);
        setError("");
    };

    const onExited = () => {
        clearState();
        getAccounts();

        if (onClosed) {
            onClosed();
        }
    }

    const setInitialCheckedFields = () => {
        const fields = Object.keys(fieldMapUser);
        let newChecked = {...checkedFields};

        for (const field of fields) {
            if (user[field] == "" && selectedAccount[field] != "") {
                newChecked[field] = 1;
            } else {
                newChecked[field] = 0;
            }
        }

        setCheckedFields(newChecked);
    }

    const handleCheckedFields = (e, field) => {
        const value = e.target.value;
        const newChecked = {...checkedFields};

        newChecked[field] = value;

        setCheckedFields(newChecked);
    }

    const renderAccountValue = field => {

        if (user[field] == "" && selectedAccount[field] == "") return null;
        else if (user[field] == "") {
            return (
                <Grid container spacing={3} key={field} alignItems="center" className={classes.gridContainer}>
                    <Grid item xs={5}></Grid>
                    <Grid item xs={2}>
                        <Typography variant="subtitle2" align='center' color="textSecondary">
                            {fieldMapUser[field]}
                        </Typography>
                    </Grid>
                    <Grid item xs={5}>
                        <Typography variant="body2" className={classes.fieldRight} color="textSecondary">
                            {selectedAccount[field]}
                        </Typography>
                    </Grid>
                </Grid>
            )
        }
        else if (selectedAccount[field] == "") {
            return (
                <Grid container spacing={3} key={field} alignItems="center" className={classes.gridContainer}>
                    <Grid item xs={5}>
                        <Typography variant="body2" className={classes.fieldLeft} color="textSecondary">
                            {user[field]}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="subtitle2" align='center' color="textSecondary">
                            {fieldMapUser[field]}
                        </Typography>
                    </Grid>
                    <Grid item xs={5}></Grid>
                </Grid>
            )
        }
        else if (user[field] != selectedAccount[field]) {
            return (
                <Grid container spacing={3} key={field} alignItems="center" className={classes.gridContainer}>
                    <Grid item xs={5}>
                        <div className={classes.fieldLeft}>
                            <FormControlLabel
                                control={
                                    <GreenRadio
                                        value={0}
                                        checked={checkedFields[field] == 0}
                                        onChange={(e) => handleCheckedFields(e, field)}
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {user[field]}
                                    </Typography>
                                }
                                labelPlacement="start"
                            />
                        </div>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="subtitle2" align='center'>
                            {fieldMapUser[field]}
                        </Typography>
                    </Grid>
                    <Grid item xs={5}>
                        <div className={classes.fieldRight}>
                            <FormControlLabel
                                control={
                                    <PurpleRadio
                                        value={1}
                                        checked={checkedFields[field] == 1}
                                        onChange={(e) => handleCheckedFields(e, field)}
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {selectedAccount[field]}
                                    </Typography>
                                }
                            />
                        </div>
                    </Grid>
                </Grid>
            )
        } else {
            return (
                <Grid container spacing={3} key={field} alignItems="center" className={classes.gridContainer}>
                    <Grid item xs={5}>
                        <Typography variant="body2" align='right' color="textSecondary">
                            {user[field]}
                        </Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="subtitle2" align='center' color="textSecondary">
                            {fieldMapUser[field]}
                        </Typography>
                    </Grid>
                    <Grid item xs={5}>
                        <Typography variant="body2" color="textSecondary">
                            {selectedAccount[field]}
                        </Typography>
                    </Grid>
                </Grid>
            )
        }
    }

    return (
        <Dialog
            aria-labelledby="reconcile-accounts-dialog-title"
            open={isOpen}
            fullWidth
            maxWidth="sm"
            onExited={onExited}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="reconcile-accounts-dialog-title">
                <span>Reconcile Accounts</span>
                <IconButton
                    aria-label="Cancel"
                    className={classes.closeButton}
                    onClick={onClose}
                    disabled={loading}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers className={classes.dialogContent}>
                {step === 1 &&
                    <Fragment>
                        <DialogContentText className={classes.dialogContentText}>
                            The following accounts are associated with <strong>{user.email}</strong>. If you recognize any of these accounts, you can merge them here.
                        </DialogContentText>

                        <List className={classes.list}>
                            {accounts.map(club => club.users.map(account => (
                                <ListItem
                                    key={account.id}
                                    button
                                    onClick={handleSelect(account)}
                                    className={classes.listItem}
                                    >
                                    <ListItemIcon>
                                        <Checkbox
                                            edge="start"
                                            color="primary"
                                            checked={selectedAccount.id === account.id}
                                            disableRipple
                                            inputProps={{ 'aria-labelledby': 'account-' + account.id }}
                                            />
                                    </ListItemIcon>
                                    <ListItemText
                                        id={'account-' + account.id}
                                        primary={
                                            <Fragment><strong>Account Name: </strong>{account.full_name}</Fragment>
                                        }
                                        secondary={
                                            <Fragment>
                                                <strong>Associated Club: </strong>
                                                <span key={club.id}>
                                                    {club.name}
                                                </span>
                                            </Fragment>
                                        }
                                    />
                                </ListItem>
                            )))}
                        </List>
                    </Fragment>
                }
                {step === 2 &&
                    <Fragment>
                        <DialogContentText className={classes.dialogContentText}>
                            Select the details you would like to use for your merged account below.
                        </DialogContentText>
                        <Grid container spacing={3} className={classes.gridContainer}>
                            <Grid item xs={5}>
                                <Typography variant="subtitle2" align='right' className={classes.green}>
                                    Current Account
                                </Typography>
                                <Divider className={classes.divider}/>
                            </Grid>
                            <Grid item xs={2}></Grid>
                            <Grid item xs={5}>
                                <Typography variant="subtitle2" className={classes.purple}>
                                    Merging Account
                                </Typography>
                                <Divider className={classes.divider}/>
                            </Grid>
                        </Grid>
                        {Object.keys(fieldMapUser).map(field => renderAccountValue(field))}

                    </Fragment>
                }
                {step === 3 &&
                    <div className={classes.stepContainer}>
                        <Zoom in={step === 3} timeout={700}>
                            <div className={classes.successIcon}>
                                <CompleteIcon color="inherit" fontSize="inherit" />
                            </div>
                        </Zoom>
                        <Typography variant="h5" color="textSecondary" gutterBottom>
                            You have successfully merged accounts.
                        </Typography>
                        <Typography variant="body1" color="textSecondary" gutterBottom>
                            You can now access clubs and features from your merged account.
                        </Typography>
                    </div>
                }
                { (error !== '') &&
                    <Typography component="div" align="center" variant="subtitle2" color="error" className={classes.marginTop}>
                        {error}
                    </Typography>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={handleBack} color="default" disabled={isBackDisabled()}>
                    {backText()}
                </Button>
                <Button onClick={handleNext} color="primary" disabled={isNextDisabled()} variant="contained">
                    {nextText()}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
